// Electricity Section component for UtilitySelector

import type { ElectricityPlan, SelectedService } from '@/types'
import type { UsageLevel } from '@/utils/planCalculations'
import { UsageLevelSelector } from './UsageLevelSelector'
import { UsageChart } from './UsageChart'

interface ElectricitySectionProps {
  electricityPlans: ElectricityPlan[]
  selectedElectricity?: SelectedService
  isExpanded: boolean
  estimatedUsage: number
  usageArray?: number[]
  usageLevel: UsageLevel
  squareFootage?: number
  isLoadingElectricity: boolean
  onToggle: () => void
  onSelectPlan: (plan: ElectricityPlan) => void
  onShowDetails: (plan: ElectricityPlan) => void
  onUsageLevelChange?: (level: UsageLevel) => void
}

export function ElectricitySection({
  electricityPlans,
  selectedElectricity,
  isExpanded,
  estimatedUsage,
  usageArray,
  usageLevel,
  squareFootage,
  isLoadingElectricity,
  onToggle,
  onSelectPlan,
  onShowDetails,
  onUsageLevelChange,
}: ElectricitySectionProps) {
  // Format usage display with property info
  const usageDisplayText = squareFootage
    ? `Based on your property (${squareFootage.toLocaleString()} sq ft), we estimate ${estimatedUsage.toLocaleString()} kWh/month`
    : `Estimated usage: ${estimatedUsage.toLocaleString()} kWh/month`

  return (
    <div
      className={`utility-selector ${isExpanded ? 'utility-selector--expanded' : ''} ${selectedElectricity ? 'utility-selector--selected' : ''}`}
      data-utility="electricity"
    >
      <div className="utility-selector__header" onClick={onToggle}>
        <div className="utility-selector__title-group">
          <div className="utility-selector__title">ELECTRICITY</div>
          <div className="utility-selector__description">
            {selectedElectricity
              ? `${selectedElectricity.name} - ${selectedElectricity.vendorName}`
              : 'Select an electricity provider'}
          </div>
        </div>
        <div className={`utility-selector__badge ${selectedElectricity ? 'utility-selector__badge--added' : 'utility-selector__badge--none'}`}>
          {selectedElectricity ? 'ADDED' : 'NONE'}
        </div>
      </div>
      <div className="utility-selector__content">
        <div className="utility-selector__section-title mobile-hide">ELECTRICITY OPTIONS FOR YOUR ADDRESS</div>

        {/* Usage display */}
        <div id="electricity-usage-display" style={{ textAlign: 'center', margin: '0 0 0.5rem 0', color: '#222222', fontSize: '14px' }}>
          {usageDisplayText}
        </div>

        {/* Usage level selector */}
        {onUsageLevelChange && (
          <UsageLevelSelector
            value={usageLevel}
            onChange={onUsageLevelChange}
            disabled={isLoadingElectricity}
          />
        )}

        {/* Usage chart */}
        {usageArray && usageArray.length === 12 && (
          <UsageChart usageArray={usageArray} />
        )}

        {isLoadingElectricity ? (
          <div className="plan-card plan-card--loading">
            <div className="plan-card__body">
              <p>Updating plans...</p>
            </div>
          </div>
        ) : electricityPlans.length === 0 ? (
          <div className="plan-card plan-card--loading">
            <div className="plan-card__body">
              <p>Loading electricity plans...</p>
            </div>
          </div>
        ) : (
          <div className="plan-grid" id="electricity-plans-container">
            {electricityPlans.map(plan => {
              const planId = plan.id || plan.ID
              const isSelected = selectedElectricity?.planId === planId
              const isFullyRenewable = plan.percentGreen === 100

              return (
                <div
                  key={planId}
                  className={`plan-card${isSelected ? ' plan-card--selected' : ''}`}
                  data-provider={plan.vendorName}
                >
                  <div className="plan-card__header">
                    <div className="plan-card__title">{plan.vendorName}</div>
                    {isFullyRenewable && (
                      <span className="plan-card__renewable-badge">100% Renewable</span>
                    )}
                  </div>
                  <div className="plan-card__logo">
                    {plan.logo && <img src={plan.logo} alt={plan.vendorName} />}
                  </div>
                  <div className="plan-card__body">
                    <div className="plan-card__row">
                      <div className="plan-card__label">Plan Name</div>
                      <div className="plan-card__value plan-card__value--small">{plan.name}</div>
                    </div>
                    {plan.pricePerKwhCents != null && (
                      <div className="plan-card__row">
                        <div className="plan-card__label">Price</div>
                        <div className="plan-card__value">{plan.pricePerKwhCents.toFixed(1)}¢/kWh</div>
                      </div>
                    )}
                    {plan.estimatedMonthlyCost != null && (
                      <div className="plan-card__row">
                        <div className="plan-card__label">Total Cost</div>
                        <div className="plan-card__value plan-card__value--highlight">${plan.estimatedMonthlyCost.toFixed(2)}/mo</div>
                      </div>
                    )}
                    {plan.termLength && (
                      <div className="plan-card__row">
                        <div className="plan-card__label">Term</div>
                        <div className="plan-card__value">{plan.termLength} months</div>
                      </div>
                    )}
                    {plan.percentGreen != null && plan.percentGreen > 0 && !isFullyRenewable && (
                      <div className="plan-card__row">
                        <div className="plan-card__label">Renewable</div>
                        <div className="plan-card__value">{plan.percentGreen.toFixed(0)}% Renewable</div>
                      </div>
                    )}
                  </div>
                  <div className="plan-card__footer">
                    <button
                      className="plan-card__button js-select-utility"
                      onClick={() => onSelectPlan(plan)}
                    >
                      {isSelected ? 'SELECTED' : 'SELECT PLAN'}
                    </button>
                    <button
                      type="button"
                      className="plan-card__details"
                      onClick={(e) => {
                        e.preventDefault()
                        onShowDetails(plan)
                      }}
                    >
                      more details
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        <div className="utility-selector__footer">
          <div className="utility-selector__footer-text">Concierge service provided by 2TurnItOn. Powered by ComparePower.</div>
        </div>
      </div>
    </div>
  )
}
